import { Switch } from '@/components/ui/switch';
import {
  useAuthoredRows,
  useCreateAuthoredRow,
  useDeleteAuthoredRow,
  useUpdateAuthoredRow,
} from 'components/IAP/hooks/icsAuthoredFormHooks';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import SaveableField from '../SaveableField';
import {
  ICS220UAS_AUTHORIZATION_FIELDS,
  ICS220UAS_AUTHORIZATION_QUESTIONS,
  ICS220UAS_BOX_LABELS,
  ICS220UAS_MISSION_COLUMNS,
  ICS220UAS_PERSONNEL_ROWS,
} from '../ics220uasBoxLabels';
import {
  BoxRowTable,
  NamedRowEditTable,
  NewCgFormCard,
  NewCgHeaderBoxes,
  NewCgPreparedByBox,
} from './newCgFormBoxes';

const PERSONNEL_BLANKS = ['Name', 'Air / Air Frequency', 'Phone Numbers'];

/**
 * ICS 220UAS-CG, boxed as the sheet prints it.
 *
 * Box 4 carries the two Yes/No questions printed above its blanks; the sheet
 * groups them, so the card does too.
 */
const Ics220UasCard = () => {
  const { effectivePeriodNumber } = useICSContext();
  const { data: summaries = [] } = useAuthoredRows(
    'uasSummaries',
    effectivePeriodNumber
  );
  const { data: missions = [] } = useAuthoredRows(
    'uasMissions',
    effectivePeriodNumber
  );
  const createSummary = useCreateAuthoredRow('uasSummaries');
  const updateSummary = useUpdateAuthoredRow('uasSummaries');
  const createMission = useCreateAuthoredRow('uasMissions');
  const updateMission = useUpdateAuthoredRow('uasMissions');
  const deleteMission = useDeleteAuthoredRow('uasMissions');
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_220UAS',
    effectivePeriodNumber
  );
  const onSuccess = () => recordVersionSave.mutate();
  const header = summaries[0] ?? null;

  const personnel =
    (header?.personnel as Record<string, Record<string, string>>) ?? {};

  const saveHeader = (patch: Record<string, unknown>) => {
    if (!header) {
      createSummary.mutate(
        { operational_period_number: effectivePeriodNumber, ...patch },
        { onSuccess }
      );
      return;
    }
    updateSummary.mutate(
      { entityId: String(header.summary_id), ...patch },
      { onSuccess }
    );
  };

  return (
    <NewCgFormCard
      title="UAS Air Operations Summary ICS-220UAS"
      formname="ICS_220UAS_CG"
      baseId="ICS_220UAS"
      exportLabel="Preview ICS-220UAS"
    >
      <NewCgHeaderBoxes labels={ICS220UAS_BOX_LABELS} testIdPrefix="ics220uas" />

      <Ics204CardSection
        boxLabel={ICS220UAS_BOX_LABELS.specialProcess}
        testId="ics220uas-section-special-process"
        className="tw:mt-0"
      >
        <div className="tw:flex tw:flex-wrap tw:gap-6">
          {ICS220UAS_AUTHORIZATION_QUESTIONS.map((question) => (
            <label
              key={question.key}
              className="tw:flex tw:items-center tw:gap-3 tw:text-sm"
            >
              <Switch
                checked={!!header?.[question.key]}
                onCheckedChange={(checked) =>
                  saveHeader({ [question.key]: checked })
                }
              />
              <span>{question.label}</span>
              {/* The sheet prints a Yes/No pair, so the switch states its
                  answer rather than repeating the question. */}
              <span className="tw:text-muted-foreground">
                {header?.[question.key] ? 'Yes' : 'No'}
              </span>
            </label>
          ))}
        </div>
        <div className="tw:mt-3 tw:grid tw:gap-3 tw:sm:grid-cols-2">
          {ICS220UAS_AUTHORIZATION_FIELDS.map((field) => (
            <SaveableField
              key={field.key}
              id={`ics220uas-${field.key}`}
              label={field.label}
              value={String(header?.[field.key] ?? '')}
              onSave={(value) => saveHeader({ [field.key]: value })}
            />
          ))}
        </div>
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS220UAS_BOX_LABELS.personnel}
        testId="ics220uas-section-personnel"
        className="tw:mt-0"
      >
        <NamedRowEditTable
          rowHeader="Position"
          rows={ICS220UAS_PERSONNEL_ROWS}
          blanks={PERSONNEL_BLANKS}
          values={personnel}
          testId="ics220uas-personnel-table"
          onSave={(rowKey, next) =>
            saveHeader({ personnel: { ...personnel, [rowKey]: next } })
          }
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS220UAS_BOX_LABELS.remarks}
        testId="ics220uas-section-remarks"
        className="tw:mt-0"
      >
        <SaveableField
          id="ics220uas-remarks"
          label="Remarks"
          hideLabel
          multiline
          placeholder="Enter remarks..."
          value={String(header?.remarks ?? '')}
          onSave={(value) => saveHeader({ remarks: value })}
        />
      </Ics204CardSection>

      <Ics204CardSection
        boxLabel={ICS220UAS_BOX_LABELS.flightDetails}
        testId="ics220uas-section-flight-details"
        className="tw:mt-0"
      >
        <BoxRowTable
          columns={ICS220UAS_MISSION_COLUMNS}
          rows={missions}
          identityColumn="mission_id"
          testId="ics220uas-missions-table"
          addLabel="Add Mission"
          onSave={(entityId, patch) =>
            updateMission.mutate({ entityId, ...patch }, { onSuccess })
          }
          onDelete={(entityId) => deleteMission.mutate(entityId, { onSuccess })}
          onAdd={async () => {
            const created = await createMission.mutateAsync({
              operational_period_number: effectivePeriodNumber,
            });
            onSuccess();
            return String(created?.mission_id ?? '');
          }}
        />
      </Ics204CardSection>

      <NewCgPreparedByBox
        baseId="ICS_220UAS"
        boxLabel={ICS220UAS_BOX_LABELS.preparedBy}
        testIdPrefix="ics220uas"
      />
    </NewCgFormCard>
  );
};

export default Ics220UasCard;
